import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { deviceFilterParams, useDevices, type DeviceFilters } from "../../api/devices";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useCursorPaging } from "../../hooks/useCursorPaging";
import { DeviceEventLog } from "../../components/DeviceEventLog";
import { StatusBadge } from "../../components/StatusBadge";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// DeviceLogsPage is the top-level "Device logs" view. Events are stored per
// device, so this is a searchable list of all devices (left) with the
// selected device's activity timeline (right). Pick or search a device to
// drill into its connect/disconnect, firmware, and update history.
//
// The device list is searched and paged on the server, so every device in
// the tenant can be found — not just the first page.
export function DeviceLogsPage() {
  const [search, setSearch] = useState("");
  const [imei, setImei] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const term = debouncedSearch.trim();
  // One box: an all-digit term is an IMEI, anything else a device name.
  const filters: DeviceFilters = { search: term, searchField: /^\d+$/.test(term) ? "imei" : "deviceName" };
  const paging = useCursorPaging(deviceFilterParams(filters).toString());
  const { data, isLoading, error, isPlaceholderData } = useDevices({ ...filters, limit: PAGE_SIZE, cursor: paging.cursor });
  // useMemo so the array identity is stable (the default-selection effect
  // depends on it).
  const devices = useMemo(() => data?.data ?? [], [data]);
  const hasMore = !!data?.pagination.hasMore;

  // Default to the first device once loaded so the page isn't empty.
  useEffect(() => {
    if (!imei && devices.length > 0) {
      setImei(devices[0].imei);
    }
  }, [devices, imei]);

  // Keep the chosen device's details while browsing other pages.
  const [selected, setSelected] = useState<(typeof devices)[number] | undefined>();
  useEffect(() => {
    const onPage = devices.find((d) => d.imei === imei);
    if (onPage) setSelected(onPage);
  }, [devices, imei]);

  return (
    <div className="p-6">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Device logs</h1>
      <p className="mb-4 mt-0.5 text-xs text-gray-500 dark:text-riot-navy-400">
        Search a device and drill into its activity — connect/disconnect, firmware reports, and update progress.
      </p>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left: search + device list */}
        <div className="lg:col-span-1">
          <div className="relative mb-2">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              aria-label="Search devices"
              placeholder="Search by name or IMEI"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
            />
          </div>

          <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-gray-200 dark:border-riot-navy-700">
            {isLoading && <p className="p-3 text-xs text-gray-400 dark:text-riot-navy-400">Loading devices…</p>}
            {error && <p className="p-3 text-xs text-red-600 dark:text-red-400">Failed to load devices</p>}
            {!isLoading && !error && devices.length === 0 && (
              <p className="p-3 text-xs text-gray-400 dark:text-riot-navy-400">
                {hasMore ? (
                  <>
                    No matches in the devices checked so far.{" "}
                    <button
                      type="button"
                      onClick={() => data?.pagination.cursor && paging.next(data.pagination.cursor, 0)}
                      className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400"
                    >
                      Keep searching
                    </button>
                  </>
                ) : term ? "No devices match your search." : "No devices."}
              </p>
            )}
            <ul>
              {devices.map((d) => (
                <li key={d.imei}>
                  <button
                    type="button"
                    onClick={() => setImei(d.imei)}
                    aria-pressed={d.imei === imei}
                    className={`flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-0 dark:border-riot-navy-800 ${
                      d.imei === imei
                        ? "bg-riot-gold-50 dark:bg-riot-navy-700"
                        : "hover:bg-gray-50 dark:hover:bg-riot-navy-800"
                    }`}
                  >
                    <StatusBadge status={d.status} variant="dot" />
                    <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-riot-navy-100">
                      {d.deviceName || d.imei}
                    </span>
                    <span className="flex-shrink-0 font-mono text-[11px] text-gray-400 dark:text-riot-navy-400">{d.imei}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {(paging.page > 1 || hasMore) && (
            <div className="mt-2 flex items-center justify-end gap-2 text-xs text-gray-500 dark:text-riot-navy-300">
              <span>Page {paging.page}</span>
              <button
                type="button"
                onClick={paging.prev}
                disabled={paging.page <= 1}
                className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-40 dark:border-riot-navy-600"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => {
                  const next = data?.pagination.cursor;
                  if (next && !isPlaceholderData) paging.next(next, devices.length);
                }}
                disabled={!hasMore}
                className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-40 dark:border-riot-navy-600"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Right: selected device's activity */}
        <div className="lg:col-span-2">
          {selected ? (
            <>
              <div className="mb-2 flex items-center gap-2 text-sm">
                <span className="font-semibold text-gray-900 dark:text-white">{selected.deviceName || "Device"}</span>
                <span className="font-mono text-xs text-gray-500 dark:text-riot-navy-400">{selected.imei}</span>
                <StatusBadge status={selected.status} variant="dot" />
              </div>
              <DeviceEventLog imei={selected.imei} />
            </>
          ) : (
            devices.length > 0 && (
              <p className="text-sm text-gray-400 dark:text-riot-navy-400">Select a device to view its activity.</p>
            )
          )}
        </div>
      </div>
    </div>
  );
}
